import { appLocale } from "../../../locales/display.ts";
import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import type { IClient } from "src/services/ClientService";
import { useRouter } from "src/services/Router";
import { ClientFinanceReport } from "./ClientFinanceReport";
import { VBox } from "@ui/box/Box";
import { Select } from "@ui/select/Select";
import Button from "@ui/button/Button";
import Input from "@ui/input/Input";
import { createName } from "src/view/FormParser";
import { useHasPermission, useHasRole, useProjectRoles } from "src/hooks/tools";
import { useClientList } from "./useClientList";
import { Pagination } from "../Pagination";
import { canSortClientList, getClientFinanceAccess, type ClientListSort } from "src/services/clientListRequest";
import { ClientPortfolio, clientSortOptions } from "./ClientPortfolio";
import { ClientFilters } from "./ClientPortfolio.styles";

export interface IClientsProps {
	acquirerId?: number;
	channelId?: number;
	terminalId?: number;
}

const periods = [{ value: 1, get name() { return msg("lastDay"); } }, { value: 7, get name() { return msg("last7Days"); } }, { value: 14, get name() { return msg("last14Days"); } }];

export const Clients = ({ acquirerId, channelId, terminalId, hideControls }: IClientsProps & { hideControls?: boolean }) => {
	useLocale();
	const canEdit = useHasPermission("clients.edit");
	const isMerchant = useHasRole("merchant");
	const isAccountant = useHasRole("accountant");
	const isSupport = useHasRole("support");
	const financeAccess = getClientFinanceAccess(useProjectRoles());
	const hasFinance = financeAccess !== "none";
	const usesMerchantRegistration = isMerchant && !canEdit && !isAccountant && !isSupport;
	const [status, setStatus] = useState("active");
	const [search, setSearch] = useState("");
	const [sort, setSort] = useState<ClientListSort>("created_at");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
	const [financeDays, setFinanceDays] = useState(7);
	const [perPage, setPerPage] = useState(20);
	const [pageSelection, setPageSelection] = useState({ key: "", page: 0 });
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [statusesReq] = GD.B_CLIENT_STATUSES.use({});
	const statusOptions = [{ value: "", name: msg("allStatuses") }, ...Array.from(new Set(["active", ...(statusesReq?.data ?? []).map(item => item.name).filter(Boolean)]))
		.map(value => ({ value, name: createName(value) }))];
	const effectiveSort = canSortClientList(sort, financeAccess) ? sort : "created_at";
	const sortOptions = clientSortOptions.filter(option => canSortClientList(option.value, financeAccess));
	const filterKey = JSON.stringify([project?.id, acquirerId, channelId, terminalId, status, search.trim(), effectiveSort, sortDir, perPage, financeAccess, hasFinance ? financeDays : undefined]);
	const page = pageSelection.key === filterKey ? pageSelection.page : 0;
	const changePage = (next: number) => setPageSelection({ key: filterKey, page: next });
	const { data, error, busy, retry } = useClientList({
		...(acquirerId == null && channelId == null && terminalId == null ? { with_payout_period: 1 as const } : {}),
		acquirerId, channelId, terminalId, status: status || undefined,
		search: search.trim() || undefined, page, per_page: perPage, sort: effectiveSort, sort_dir: sortDir,
		...(hasFinance ? { finance_days: financeDays } : {}),
	}, project?.id, financeAccess);
	const router = useRouter();

	useEffect(() => {
		if (data && page > 0 && page * data.per_page >= data.total) changePage(Math.max(0, Math.ceil(data.total / data.per_page) - 1));
	}, [data, page, filterKey]);

	const reset = () => {
		setStatus("active"); setSearch(""); setSort("created_at"); setSortDir("desc"); setFinanceDays(7);
		setPageSelection({ key: "", page: 0 });
	};
	const open = (client: IClient) => {
		if (["created", "rejected"].includes(client.status) && usesMerchantRegistration) router.navigate("registerClient", { rid: String(client.id) });
		else router.navigate("client", { cid: String(client.id) });
	};

	return <VBox gap="16px">
		{!hideControls && <ClientFilters>
			<div className="search-field"><Input size="small" type="search" name="search" placeholder={msg("clientNameOrId")} data={{ search }} label={msg("searchClients")} onChange={value => setSearch(value)} /></div>
			<div className="filter-field"><Select size="small" label={msg("status")} values={statusOptions} value={statusOptions.find(option => option.value === status)} onChange={value => setStatus(String(value))} /></div>
			{hasFinance && <div className="filter-field"><Select size="small" label={msg("financialPeriod")} values={periods} value={periods.find(option => option.value === financeDays)} onChange={value => setFinanceDays(Number(value))} /></div>}
			<div className="filter-field"><Select size="small" label={msg("sortBy")} values={sortOptions} value={sortOptions.find(option => option.value === effectiveSort)} onChange={value => setSort(value as unknown as ClientListSort)} /></div>
			<div className="filter-field"><Select size="small" label={msg("order")} values={[{ value: "desc", name: msg("descending") }, { value: "asc", name: msg("ascending") }]} value={{ value: sortDir, name: sortDir === "desc" ? "Descending" : "Ascending" }} onChange={value => setSortDir(value as unknown as "asc" | "desc")} /></div>
			<div className="reset"><Button onClick={reset} variant="secondarySmall" title={msg("reset")} /></div>
		</ClientFilters>}
		<ClientPortfolio data={data} error={error} busy={busy} financeAccess={financeAccess} sort={effectiveSort} sortDir={sortDir}
			onSort={setSort} onDirection={() => setSortDir(value => value === "asc" ? "desc" : "asc")} onRetry={retry} onOpen={open}
			renderReport={financeAccess === "full" ? client => <ClientFinanceReport client={client} metadata={data?.finance} /> : undefined}
			footer={<>
				{data && data.total > data.per_page && <Pagination disabled={busy} pagination={{ total: data.total, page: data.page, perPage: data.per_page, totalPages: Math.ceil(data.total / data.per_page) }} onPageChange={changePage} />}
				{data && data.total <= data.per_page && <span>{data.total === 0 ? msg("zeroClients") : msg("template1OfClients_6baf5e1f", { v0: data.items.length, v1: data.total.toLocaleString(appLocale("en-US")) })}</span>}
				<label className="page-size">{msg("perPage")}<select aria-label={msg("clientsPerPage")} value={perPage} onChange={event => setPerPage(Number(event.target.value))}>{[20, 50, 100].map(value => <option key={value} value={value}>{value}</option>)}</select></label>
			</>} />
	</VBox>;
};
